import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import leadsService from '../../services/leadsService';
import './LeadImport.css';

const SOURCE_CHANNEL = 'Apple A Day';

const TARGET_FIELDS = [
  { value: 'name',       label: 'Name' },
  { value: 'phone',      label: 'Phone' },
  { value: 'area',       label: 'Area / Address' },
  { value: 'notes',      label: 'Notes' },
  { value: 'source_ref', label: 'Source Ref' },
  { value: 'lead_date',  label: 'Lead Date' },
  { value: 'ignore',     label: '— Ignore —' },
];

const autoSuggest = (col) => {
  const c = col.toLowerCase().trim();
  if (c === 'header date' || c === 'collection date')                          return 'lead_date';
  if (c === 'payee name')                                                      return 'name';
  if (c.includes('phone') || c.includes('mobile'))                             return 'phone';
  if (c.includes('address') || c.includes('pincode'))                          return 'area';
  if (c === 'remarks')                                                         return 'ignore';
  if (c.includes('note') || c.includes('remark') || c.includes('goal sheet')
      || c.includes('customer 1') || c.includes('gender'))                     return 'notes';
  return 'ignore';
};

const cleanPhone = (raw) => {
  if (!raw) return '';
  let s = String(raw).replace(/\s+/g, '').replace(/\+/g, '');
  if (s.startsWith('91') && s.length > 10) s = s.slice(2);
  s = s.replace(/\D/g, '');
  return s.length === 10 ? s : '';
};

const cleanText = (raw) => {
  if (!raw) return '';
  return String(raw).trim().replace(/_/g, ' ');
};

const toTitleCase = (s) =>
  s.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());

const cleanLeadDate = (raw) => {
  if (!raw) return null;
  if (raw instanceof Date) {
    try { return raw.toISOString(); } catch { return null; }
  }
  try { return new Date(raw).toISOString(); } catch { return null; }
};

const applyMappings = (rawRow, mappings) => {
  const result = { name: '', phone: '', area: '', notes: '', source_ref: '', lead_date: null };
  const noteParts = [];
  const srcParts  = [];
  const areaParts = [];

  for (const [col, field] of Object.entries(mappings)) {
    if (field === 'ignore') continue;
    const val = rawRow[col];
    if (val === undefined || val === null || val === '') continue;

    switch (field) {
      case 'name':       result.name      = toTitleCase(cleanText(val)); break;
      case 'phone':      result.phone     = cleanPhone(val); break;
      case 'area':       areaParts.push(cleanText(val)); break;
      case 'notes':      noteParts.push(cleanText(val)); break;
      case 'source_ref': srcParts.push(cleanText(val)); break;
      case 'lead_date':  result.lead_date = cleanLeadDate(val); break;
      default: break;
    }
  }

  result.area       = areaParts.filter(Boolean).join(', ');
  result.notes      = noteParts.filter(Boolean).join(' | ');
  result.source_ref = srcParts.filter(Boolean).join(' — ');
  return result;
};

const parseExcelFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb   = XLSX.read(data, { type: 'array', cellDates: true });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!rows.length) { reject(new Error('File appears empty or has no data rows')); return; }
        const headers = Object.keys(rows[0]);
        resolve({ headers, rows });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });

const sampleValue = (rows, col) => {
  for (const row of rows.slice(0, 3)) {
    const v = String(row[col] ?? '').trim();
    if (v) return v.length > 40 ? v.slice(0, 40) + '…' : v;
  }
  return '—';
};

// ─── Main component ───────────────────────────────────────────────────────────
const LeadImportAAD = () => {
  const fileInputRef = useRef(null);

  const [step, setStep]             = useState('upload');
  const [file, setFile]             = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [mappings, setMappings]     = useState({});
  const [showMapping, setShowMapping] = useState(false);

  const [processedRows, setProcessedRows] = useState([]);
  const [validation, setValidation]       = useState(null);
  const [skipExisting, setSkipExisting]   = useState(true);

  const [importing, setImporting]       = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f) processFile(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) processFile(f);
  };

  const processFile = async (f) => {
    setFile(f);
    setError('');
    setLoading(true);
    try {
      const parsed = await parseExcelFile(f);
      setParsedData(parsed);

      const defaultMaps = {};
      parsed.headers.forEach(h => { defaultMaps[h] = autoSuggest(h); });
      setMappings(defaultMaps);
      setShowMapping(true);
    } catch (err) {
      setError('Failed to parse file: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const buildPreview = async (maps) => {
    setLoading(true);
    try {
      const mapsToUse = maps || mappings;
      const phonesSeen = new Set();
      const processed = parsedData.rows.map(row => {
        const clean = applyMappings(row, mapsToUse);
        let status = 'valid';
        if (!clean.phone)                         status = 'invalid_phone';
        else if (phonesSeen.has(clean.phone))     status = 'duplicate_in_file';
        else                                       phonesSeen.add(clean.phone);
        return { ...clean, _status: status };
      });
      setProcessedRows(processed);

      const validPhones = [...new Set(processed.filter(r => r._status === 'valid').map(r => r.phone))];
      const valRes = await leadsService.validateImportPhones(validPhones, []);
      setValidation(valRes.data);

      setShowMapping(false);
      setStep('preview');
    } catch (err) {
      setError('Failed to prepare preview: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    setError('');
    try {
      const existingLeads     = new Set(validation?.existing_leads     || []);
      const existingCustomers = new Set(validation?.existing_customers || []);

      const rowsToSend = processedRows
        .filter(r => {
          if (r._status === 'invalid_phone')    return false;
          if (r._status === 'duplicate_in_file') return false;
          if (existingCustomers.has(r.phone))   return false;
          return true;
        })
        .map(({ _status, ...rest }) => rest);

      const res = await leadsService.importLeads({
        rows: rowsToSend,
        filename: file?.name,
        skip_existing: skipExisting,
        source_channel: SOURCE_CHANNEL,
      });

      setImportResult(res.data);
      setStep('results');
    } catch (err) {
      setError('Import failed: ' + err.message);
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setStep('upload');
    setFile(null);
    setParsedData(null);
    setMappings({});
    setShowMapping(false);
    setProcessedRows([]);
    setValidation(null);
    setImportResult(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const stats = (() => {
    if (!processedRows.length) return null;
    const existingLeads     = new Set(validation?.existing_leads     || []);
    const existingCustomers = new Set(validation?.existing_customers || []);
    const invalid     = processedRows.filter(r => r._status === 'invalid_phone').length;
    const dupInFile   = processedRows.filter(r => r._status === 'duplicate_in_file').length;
    const validRows   = processedRows.filter(r => r._status === 'valid');
    const dupInDB     = validRows.filter(r => existingLeads.has(r.phone) || existingCustomers.has(r.phone)).length;
    const willImport  = validRows.length - (skipExisting ? dupInDB : [...existingCustomers].filter(p => validRows.some(r => r.phone === p)).length);
    return { total: processedRows.length, invalid, dupInFile, dupInDB, willImport: Math.max(0, willImport) };
  })();

  const steps = ['Upload', 'Preview', 'Done'];
  const stepIndex = step === 'upload' ? 0 : step === 'preview' ? 1 : 2;

  return (
    <div className="lead-import">
      <div className="section-header">
        <div>
          <h2>Import Leads — Apple A Day</h2>
          <p>Upload Excel (.xlsx) files from Apple A Day to bulk-create leads</p>
        </div>
        <Link to="/leads" className="btn-cancel" style={{ textDecoration: 'none', padding: '0.5rem 1rem' }}>
          ← Back to Leads
        </Link>
      </div>

      <div className="import-step-bar">
        {steps.map((s, i) => (
          <div key={s} className={`import-step-item ${i === stepIndex ? 'active' : ''} ${i < stepIndex ? 'done' : ''}`}>
            <div className="import-step-dot">{i < stepIndex ? '✓' : i + 1}</div>
            <span className="import-step-label">{s}</span>
            {i < steps.length - 1 && <div className="import-step-line" />}
          </div>
        ))}
      </div>

      {error && <div className="error-message" style={{ margin: '0 0 1rem' }}>{error}</div>}

      {/* ── STEP 1: Upload ── */}
      {step === 'upload' && (
        <div className="import-card">
          {!file && (
            <div
              className="import-dropzone"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={e => e.preventDefault()}
            >
              <div className="import-dropzone-icon">📂</div>
              <p className="import-dropzone-title">Drop Apple A Day Excel file here or click to browse</p>
              <p className="import-dropzone-hint">Accepts .xlsx files</p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
            </div>
          )}

          {loading && (
            <div className="import-loading">
              <div className="import-spinner" />
              <p>Parsing file…</p>
            </div>
          )}

          {!loading && file && showMapping && parsedData && (
            <>
              <div className="import-mapping-header">
                <div>
                  <h3 className="import-section-title">Map Columns</h3>
                  <p className="import-section-sub">
                    File: <strong>{file.name}</strong> · {parsedData.rows.length} rows detected
                  </p>
                </div>
                <button className="btn-cancel" onClick={handleReset}>Change File</button>
              </div>

              <div className="import-mapping-table-wrap">
                <table className="import-mapping-table">
                  <thead>
                    <tr>
                      <th>Excel Column</th>
                      <th>Sample Data</th>
                      <th>Maps To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedData.headers.map(col => (
                      <tr key={col}>
                        <td className="import-col-name">{col}</td>
                        <td className="import-col-sample">{sampleValue(parsedData.rows, col)}</td>
                        <td>
                          <select
                            className="import-field-select"
                            value={mappings[col] || 'ignore'}
                            onChange={e => setMappings(prev => ({ ...prev, [col]: e.target.value }))}
                          >
                            {TARGET_FIELDS.map(f => (
                              <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="import-mapping-actions">
                <button className="btn-cancel" onClick={handleReset}>Cancel</button>
                <button className="btn-submit" onClick={() => buildPreview(null)} disabled={loading}>
                  Preview →
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── STEP 2: Preview ── */}
      {step === 'preview' && processedRows.length > 0 && (
        <div className="import-card">
          <div className="import-preview-header">
            <div>
              <h3 className="import-section-title">Preview</h3>
              <p className="import-section-sub">File: <strong>{file?.name}</strong></p>
            </div>
            <button className="btn-cancel" onClick={handleReset}>Change File</button>
          </div>

          {stats && (
            <div className="import-stats-row">
              <div className="import-stat import-stat--total">
                <span className="import-stat-num">{stats.total}</span>
                <span className="import-stat-lbl">Total rows</span>
              </div>
              <div className="import-stat import-stat--good">
                <span className="import-stat-num">{stats.total - stats.invalid - stats.dupInFile}</span>
                <span className="import-stat-lbl">Valid phones</span>
              </div>
              {stats.invalid > 0 && (
                <div className="import-stat import-stat--warn">
                  <span className="import-stat-num">{stats.invalid}</span>
                  <span className="import-stat-lbl">Invalid phone</span>
                </div>
              )}
              {stats.dupInFile > 0 && (
                <div className="import-stat import-stat--warn">
                  <span className="import-stat-num">{stats.dupInFile}</span>
                  <span className="import-stat-lbl">Duplicate in file</span>
                </div>
              )}
              {stats.dupInDB > 0 && (
                <div className="import-stat import-stat--db">
                  <span className="import-stat-num">{stats.dupInDB}</span>
                  <span className="import-stat-lbl">Already in DB</span>
                </div>
              )}
            </div>
          )}

          {validation && validation.existing_leads.length > 0 && (
            <div className="import-dedup-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={skipExisting}
                  onChange={e => setSkipExisting(e.target.checked)}
                />
                Skip leads already in the system ({validation.existing_leads.length} existing)
              </label>
            </div>
          )}

          <div className="import-preview-table-wrap">
            <table className="orders-table import-preview-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Status</th>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Area</th>
                  <th>Notes</th>
                  <th>Lead Date</th>
                </tr>
              </thead>
              <tbody>
                {processedRows.map((row, i) => {
                  const existLead = validation?.existing_leads?.includes(row.phone);
                  const existCust = validation?.existing_customers?.includes(row.phone);
                  let rowClass = '';
                  let badge = null;
                  if (row._status === 'invalid_phone') {
                    rowClass = 'import-row--skip';
                    badge = <span className="import-badge import-badge--error">Invalid phone</span>;
                  } else if (row._status === 'duplicate_in_file') {
                    rowClass = 'import-row--skip';
                    badge = <span className="import-badge import-badge--warn">Dup in file</span>;
                  } else if (existCust) {
                    rowClass = 'import-row--skip';
                    badge = <span className="import-badge import-badge--warn">Customer exists</span>;
                  } else if (existLead) {
                    rowClass = skipExisting ? 'import-row--skip' : '';
                    badge = <span className="import-badge import-badge--db">{skipExisting ? 'Lead exists — skip' : 'Lead exists — import'}</span>;
                  } else {
                    badge = <span className="import-badge import-badge--ok">New</span>;
                  }
                  return (
                    <tr key={i} className={rowClass}>
                      <td style={{ color: '#999', fontSize: '0.8rem' }}>{i + 1}</td>
                      <td>{badge}</td>
                      <td>{row.name || <em style={{ color: '#aaa' }}>—</em>}</td>
                      <td style={{ fontFamily: 'monospace' }}>{row.phone || <em style={{ color: '#e74c3c' }}>missing</em>}</td>
                      <td>{row.area || '—'}</td>
                      <td style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.notes || '—'}</td>
                      <td>{row.lead_date ? new Date(row.lead_date).toLocaleDateString() : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="import-preview-actions">
            <button className="btn-cancel" onClick={handleReset}>Cancel</button>
            <button className="btn-submit" onClick={handleImport} disabled={importing}>
              {importing
                ? 'Importing…'
                : `Import ${stats?.willImport ?? '?'} Lead${stats?.willImport !== 1 ? 's' : ''} →`}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Results ── */}
      {step === 'results' && importResult && (
        <div className="import-card">
          <h3 className="import-section-title">Import Complete</h3>
          <div className="import-result-grid">
            <div className="import-result-item import-result--green">
              <span className="import-result-num">{importResult.imported}</span>
              <span className="import-result-lbl">Leads Created</span>
            </div>
            <div className="import-result-item import-result--grey">
              <span className="import-result-num">{importResult.skipped}</span>
              <span className="import-result-lbl">Skipped</span>
            </div>
            {importResult.errors > 0 && (
              <div className="import-result-item import-result--red">
                <span className="import-result-num">{importResult.errors}</span>
                <span className="import-result-lbl">Errors</span>
              </div>
            )}
          </div>

          {importResult.errors > 0 && importResult.error_details?.length > 0 && (
            <div className="import-errors-section">
              <p className="import-section-title" style={{ fontSize: '0.9rem' }}>Error Details</p>
              {importResult.error_details.map((e, i) => (
                <div key={i} className="import-error-row">
                  <span style={{ fontFamily: 'monospace' }}>{e.phone}</span>
                  <span style={{ color: '#e74c3c', marginLeft: '1rem' }}>{e.error}</span>
                </div>
              ))}
            </div>
          )}

          <div className="import-preview-actions">
            <button className="btn-cancel" onClick={handleReset}>Import Another File</button>
            <Link to="/leads" className="btn-submit" style={{ textDecoration: 'none' }}>
              Go to Leads →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadImportAAD;
